import { useCallback, useEffect, useRef, useState } from 'react';
import { meetingService } from '../../../services/meeting.service.ts';
import {
  getMeetingDurationLimitSeconds,
  getUserPlan,
  MEETING_LIMIT_WARNING_SECONDS,
  resolveRoomDurationLimitSeconds,
} from '../../../utils/plan.ts';
import {
  createRoomTimePolicy,
  getElapsedSeconds,
  getRemainingSeconds,
  isPolicyExpired,
  readRoomTimePolicy,
  RoomTimePolicy,
  writeRoomTimePolicy,
} from '../utils/roomTimePolicy.ts';
import { authService } from '../../../services/auth.service.ts';
import { isLiveSessionExpired } from '../utils/meetingLiveExpiry.ts';

async function fetchMeetingByCodeOrId(meetingKey: string) {
  try {
    return await meetingService.getMeetingByCode(meetingKey);
  } catch {
    // Toplantı kodu DB'de yoksa (P2P anlık oda) public uçlar yeterli; /api/meetings/:id çağırma
    return null;
  }
}

export function useMeetingTimeLimit(
  meetingId: string | undefined,
  isGuest: boolean,
  onTimeExpired: () => void
) {
  const [policy, setPolicy] = useState<RoomTimePolicy | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const expiredCalledRef = useRef(false);
  const onTimeExpiredRef = useRef(onTimeExpired);
  onTimeExpiredRef.current = onTimeExpired;

  const userPlan = getUserPlan(authService.getUser());
  const planMaxLimitSeconds = getMeetingDurationLimitSeconds(userPlan);

  const applyPolicy = useCallback(
    (next: RoomTimePolicy, meetingKey: string) => {
      writeRoomTimePolicy(meetingKey, next);
      setPolicy(next);
      setElapsedSeconds(getElapsedSeconds(next));
    },
    []
  );

  const applyRemotePolicy = useCallback(
    (incoming: RoomTimePolicy) => {
      if (!meetingId) return;
      const existing = readRoomTimePolicy(meetingId);
      if (existing && existing.startedAt <= incoming.startedAt) {
        return;
      }
      applyPolicy(incoming, meetingId);
    },
    [meetingId, applyPolicy]
  );

  useEffect(() => {
    expiredCalledRef.current = false;
    if (!meetingId) {
      setPolicy(null);
      setElapsedSeconds(0);
      return;
    }

    const stored = readRoomTimePolicy(meetingId);
    if (stored) {
      setPolicy(stored);
      setElapsedSeconds(getElapsedSeconds(stored));
      if (isPolicyExpired(stored)) {
        expiredCalledRef.current = true;
        onTimeExpiredRef.current();
      }
    }

    let cancelled = false;

    (async () => {
      try {
        const meeting = await fetchMeetingByCodeOrId(meetingId);
        if (cancelled) return;

        if (!meeting || meeting.meetingFormat === 'IN_PERSON') {
          if (!stored) {
            setPolicy(null);
            setElapsedSeconds(0);
          }
          return;
        }

        if (meeting.status === 'LIVE' && isLiveSessionExpired(meeting)) {
          expiredCalledRef.current = true;
          onTimeExpiredRef.current();
          return;
        }

        const roomLimitSeconds = resolveRoomDurationLimitSeconds(
          userPlan,
          meeting.duration
        );

        // API duration döndürmüyorsa: mevcut sessionStorage politikasını koru
        // (MeetingCard başlatırken doğru süreyi yazar). Yoksa plan üst sınırı.
        if (roomLimitSeconds == null) {
          if (!stored && planMaxLimitSeconds == null) {
            setPolicy(null);
            setElapsedSeconds(0);
          } else if (!stored && planMaxLimitSeconds != null) {
            applyPolicy(createRoomTimePolicy(planMaxLimitSeconds), meetingId);
          }
          return;
        }

        // Planlanan süre geldiyse sessionStorage'daki yanlış 50dk limitini düzelt
        if (meeting.duration != null && Number(meeting.duration) > 0) {
          const startedAt =
            stored?.startedAt ??
            (meeting.startedAt ? new Date(meeting.startedAt).getTime() : Date.now());
          if (!stored || stored.limitSeconds !== roomLimitSeconds) {
            applyPolicy(createRoomTimePolicy(roomLimitSeconds, startedAt), meetingId);
          }
          return;
        }

        if (!stored) {
          const startedAt = meeting.startedAt
            ? new Date(meeting.startedAt).getTime()
            : Date.now();
          applyPolicy(createRoomTimePolicy(roomLimitSeconds, startedAt), meetingId);
        }
      } catch {
        if (cancelled) return;
        if (!stored && !isGuest && planMaxLimitSeconds != null) {
          applyPolicy(createRoomTimePolicy(planMaxLimitSeconds), meetingId);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [meetingId, isGuest, userPlan, planMaxLimitSeconds, applyPolicy]);

  useEffect(() => {
    if (!policy) return;

    const tick = () => {
      const elapsed = getElapsedSeconds(policy);
      setElapsedSeconds(elapsed);

      if (isPolicyExpired(policy) && !expiredCalledRef.current) {
        expiredCalledRef.current = true;
        onTimeExpiredRef.current();
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [policy]);

  const remainingSeconds = policy ? getRemainingSeconds(policy) : null;
  const isLimited = policy != null;
  const showWarning =
    isLimited &&
    remainingSeconds != null &&
    remainingSeconds > 0 &&
    remainingSeconds <= MEETING_LIMIT_WARNING_SECONDS;

  const roomTimePolicyForSocket =
    !isGuest && policy != null ? policy : null;

  return {
    policy,
    elapsedSeconds,
    remainingSeconds,
    isLimited,
    showWarning,
    limitSeconds: policy?.limitSeconds ?? null,
    applyRemotePolicy,
    roomTimePolicyForSocket,
  };
}
